import { useCallback, useEffect, useRef, useState } from 'react';
import { eventBus, APP_EVENT_KEYS } from '@/infrastructure/eventBus';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { ContentListComponent } from '@/presentation/feature/content/list/ContentListComponent';
import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import { ContentPageEmptyState } from '@/presentation/feature/content/components/ContentPageEmptyState';
import { ContentPageErrorCard } from '@/presentation/feature/content/components/ContentPageErrorCard';
import { ContentPageFirstLoad } from '@/presentation/feature/content/components/ContentPageFirstLoad';
import { Navigator } from '@/presentation/routing/Navigator';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useMeasuredHeight } from '@/presentation/hooks/useMeasuredHeight';
import { useVideoManagement } from '@/presentation/feature/content/hooks/useVideoManagement';
import { useContentDetails } from './hooks/useContentDetails';
import type { RootState } from '@/presentation/store';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { decodeMediaPathFromRoute } from '@/presentation/utils/mediaPathUtils';
import { ContentCommentsSheet } from '@/presentation/feature/content/components/sheets/comments/ContentCommentsSheet';
import { ContentUsersLikedSheet } from '@/presentation/feature/content/components/sheets/contentUsersLiked/ContentUsersLikedSheet';
import { ContentGiftsSheet } from '@/presentation/feature/content/components/sheets/contentGifts/ContentGiftsSheet';
import type { CommentInfoEntity } from '@/domain/entities/comment/commentInfoEntity';
import { qaAttr } from '@/presentation/utils/qaAttr';

interface LocationState {
    medias?: UserMediaEntity[];
    commentInfo?: CommentInfoEntity;
    openCommentsSheet?: boolean;
    openContentLikesSheet?: boolean;
    openGiftsSheet?: boolean;
}

/**
 * Content Details Page - Displays a single content item
 *
 * Two modes:
 * 1. With initial medias from location state: Uses the first provided media
 * 2. Without initial data: Fetches the media using useContentDetails hook
 *
 * App-bar provenance: the shared `ExpauseAppBar`
 * (`presentation/components/common/appBar/ExpauseAppBar.tsx`) ports the pushed
 * content-list screen's bar
 * (`lib/presentation/screens/content/custom_widgets/scrollable/content_list_widget.dart:1112-1147`).
 * This page ports Flutter's single-media push
 * (`lib/services/push_navigation_service.dart:388-401`, `:403-417` →
 * `navigateToContentListScreen(WithoutHeroAnimation)` with `medias: [media]` +
 * `fetchSingleMediaPath` — the payload this page's `LocationState` mirrors),
 * which passes **no** `showAppBar`, so the `bool showAppBar = true` default
 * applies (`lib/services/navigation_service.dart:607`, `:668`) and Flutter
 * renders the bar. The title is **intentionally absent**: Flutter's title is an
 * empty `Container()` (`content_list_widget.dart:1126`), so the bar carries the
 * back chevron alone. The bar renders in BOTH branches — the first-load spinner
 * branch included — because Flutter keeps the same bar over its empty/first-load
 * body (`content_list_widget.dart:1029-1048`) and because this chrome-less
 * detail route ("Detail routes without TopBar", `AppRoutes.tsx`) would otherwise
 * strand a deep-linked visitor (`workflow/lessons.md`).
 *
 * Back control — DOCUMENTED, APPROVED DIVERGENCE (settled; do not re-raise):
 * this page's back control pops the route directly and does **not** close open
 * sheets first. Flutter's handler
 * (`lib/presentation/screens/content/custom_widgets/scrollable/content_list_screen.dart:177-190`)
 * does: it closes the comments sheet, then pops the topmost sheet, and only
 * reaches `NavigationService.pop()` when no sheet is open. On the web a browser
 * back press pops the page together with its open sheets — the established,
 * approved behaviour of the web modal/sheet system — and that same ruling was
 * deliberately extended to the in-app app-bar chevron so BOTH back affordances
 * on this page behave identically. Flutter has no such split (its chevron and
 * the system back gesture share one handler); porting the sheet-dismiss-first
 * branches to the chevron alone would give this page two different back
 * behaviours, which is why it is intentionally not done. This is a product
 * decision, not a technical limitation.
 *
 * Architecture: Component → Hook → UseCase → Service
 */
export const ContentDetailsPage = () => {
    const { userMediaId: rawParam } = useParams<{ userMediaId: string }>();
    const location = useLocation();
    const { l10n } = useLocale();
    const responsive = useResponsiveDesign();
    const currentUser = useSelector((state: RootState) => state.auth.user);
    const currentUserId = currentUser?.firebaseId;

    // Snapshot of `location.state` (the deep-link payload set by
    // `Navigator.navigateToContentDetails*`), taken ONCE at mount — same pattern as
    // RoadmapPage.tsx:105-118 / ChatPage.tsx:65-75 / MediaDetailsPage.tsx:74-84.
    //
    // WHY a snapshot and never the live `location.state`: this page is route-cached by
    // `KeepAliveOutlet`, which keeps cached routes MOUNTED and only flips `display`
    // (RouteStack.tsx:1004-1019). `useLocation()` reads the LIVE router context, so a
    // cached instance re-renders with whatever route is active. Reading it live let a
    // second content page (pushed with no state, e.g. from a comment's media thumbnail)
    // pick up the PREVIOUS page's `openCommentsSheet` + `commentInfo` on the way back and
    // open a duplicate comments sheet over it (user review round 2, observation 1).
    const locationStateRef = useRef<LocationState | undefined>(
        location.state as LocationState | undefined,
    );
    const locationState = locationStateRef.current;

    // Extract initial data from the snapshot
    const initialMedias = locationState?.medias;
    const initialMedia = initialMedias && initialMedias.length > 0 ? initialMedias[0] : null;

    // If the param starts with @, the rest is a relative `path` (a media's
    // `relativePath`) rather than a Firebase media ID. Mirrors the username-vs-id
    // detection in UserDetailsPage and Flutter's `fetchSingleMediaPath` flow.
    const isPath = rawParam?.startsWith('@') ?? false;
    const userMediaId = isPath ? undefined : rawParam;
    const mediaPath = isPath ? decodeMediaPathFromRoute(rawParam!.substring(1)) : undefined;

    // Get navigation screen key from RouteStack context
    // IMPORTANT: Capture the screen key ONCE on mount and never change it
    const { currentScreenKeyRef } = useKeepAlive();
    const screenKeyRef = useRef<string>(
        currentScreenKeyRef.current || generateScreenKey(location.pathname)
    );
    const screenKey = screenKeyRef.current;

    // Use feature hook for fetching media by ID
    const {
        media: fetchedMedia,
        isLoading,
        error,
        fetchMedia,
    } = useContentDetails({ userMediaId, mediaPath });

    // State to manage the media (either from location state or fetched)
    const [media, setMedia] = useState<UserMediaEntity | null>(initialMedia);
    const [isInitialized, setIsInitialized] = useState(false);

    const { autoTogglePlayPauseActiveVideo } = useVideoManagement();
    const [isPageActive, setIsPageActive] = useState(true);

    // Rendered height of the sticky app bar, fed to the list as
    // `scrollOffsetTop` so a scroll landing stops BELOW the bar rather than
    // behind it. This page renders a single media and passes no `initialIndex`,
    // so nothing lands today — it is wired anyway because the list it hands the
    // media to shares `UserVideosPage`'s landing math, and the first
    // `scrollToIndex` call added here would otherwise silently park the media's
    // author row under the bar. Measured, not recomputed: the bar's height is
    // `calc(topBar.height + safeArea.top)` and `safeArea.top` is an `env()`
    // string, not a number. Declared ABOVE the first-load early return (Rules of
    // Hooks) and handed to BOTH branches — forwarded through
    // `ContentPageFirstLoad` to the same `ExpauseAppBar` root the loaded branch
    // measures — so the callback ref re-attaches when that branch swaps.
    const [appBarHeight, appBarRef] = useMeasuredHeight();

    // Keep the locally displayed media in sync with global events.
    // These listeners are required alongside the identical ones in useContentDetails because
    // the page's `media` state is initialised from the constructed initialMedia (passed via
    // location state from WalletPage). In that path, `fetchedMedia` starts as null and the
    // sync useEffect (`if (fetchedMedia)`) can't propagate a null removal back here.
    // The page must own these handlers so that events fired against the constructed media
    // (before or after the real fetch completes) are always reflected in the displayed state.
    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_LIST_ITEM_REMOVED, ({ mediaId }) => {
            setMedia(prev => prev?.id === mediaId ? null : prev);
        });
    }, []);

    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_ITEM_LIKES_UPDATED, ({ mediaId, likes }) => {
            setMedia(prev => prev?.id === mediaId ? { ...prev, likes } : prev);
        });
    }, []);

    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_ITEM_COMMENTS_UPDATED, ({ mediaId, comments }) => {
            setMedia(prev => prev?.id === mediaId ? { ...prev, comments } : prev);
        });
    }, []);

    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_ITEM_UPDATED, ({ mediaId, patch }) => {
            setMedia(prev => prev?.id === mediaId ? { ...prev, ...patch } : prev);
        });
    }, []);

    // Base page lifecycle management (Flutter RouteObserver pattern)
    useBasePage({
        screenKey,
        onInit: () => {
            // Always fetch the full media data, even if initial media was provided
            // via location state (the initial media may be a partial construct)
            fetchMedia();
            setIsInitialized(true);

            return () => {
                // Cleanup on unmount
            };
        },
        onPush: (repushed) => {
            if (repushed) {
                autoTogglePlayPauseActiveVideo();
            }
            setIsPageActive(true);
        },
        onPopNext: () => {
            autoTogglePlayPauseActiveVideo();
            setIsPageActive(true);
        },
        onPushNext: () => {
            autoTogglePlayPauseActiveVideo();
            setIsPageActive(false);
        },
        onPop: () => {
            autoTogglePlayPauseActiveVideo();
            setIsPageActive(false);
        },
        onDispose: () => {
            // Final cleanup (rare with KeepAlive caching)
        },
        debug: false,
    });

    // Update media when fetched from hook (replaces initial or empty media with full data)
    useEffect(() => {
        if (fetchedMedia) {
            setMedia(fetchedMedia);
        }
    }, [fetchedMedia]);

    // Deep-link sheet auto-open. Mirrors Flutter's pager post-fetch branch
    // (content_list_pager_screen.dart:101-118), which runs the three flags with
    // a strict `if / else if / else if` precedence — comments > likes > gifts —
    // so at most ONE sheet opens per navigation. The comments branch also mirrors
    // `_onCommentReplyTap` (base_content_widget.dart:296): navigated here from a
    // comment-reply chip, it opens the comments sheet at the highlighted parent.
    //
    // Guarded by a single ref so exactly one sheet opens once per mount even
    // though `media` updates a few times (initial → fetched → live event
    // patches), and gated on `media` so the likes/gifts sheets read the RESOLVED
    // media's counts — Flutter reads them from the fetched document too
    // (`_getSingleMedia()` replaces `widget.medias[0]`,
    // content_list_pager_screen.dart:148-160).
    //
    // TODO: @claude add a follow up task for this: screen-scoped commentInfo on content details — Flutter feeds the ONE CommentsBottomSheet the screen-level widget.commentInfo, so the ordinary comment-icon opener keeps the deep link's highlight; the web only passes it on the auto-open — Flutter lib/presentation/screens/content/custom_widgets/pager/content_list_pager_screen.dart:267,288
    const deepLinkSheetOpenedRef = useRef(false);
    useEffect(() => {
        if (deepLinkSheetOpenedRef.current || !media) return;

        if (locationState?.openCommentsSheet) {
            deepLinkSheetOpenedRef.current = true;
            ContentCommentsSheet.show({
                media,
                commentInfo: locationState.commentInfo,
                screenKey,
            });
        } else if (locationState?.openContentLikesSheet) {
            deepLinkSheetOpenedRef.current = true;
            ContentUsersLikedSheet.show({
                mediaPath: media.relativePath,
                likes: media.likes ?? 0,
                screenKey,
            });
        } else if (locationState?.openGiftsSheet) {
            deepLinkSheetOpenedRef.current = true;
            ContentGiftsSheet.show({
                mediaPath: media.relativePath,
                coins: media.coins ?? 0,
                screenKey,
            });
        }
    }, [media, locationState, screenKey]);

    // Leading (back) handler for the app bar. Declared ABOVE the first-load
    // early return so both render branches share one instance and the hook
    // order is identical in every state.
    //
    // Deep-link guard: with no in-app history to pop, fall back to the feed
    // instead of letting `goBack()` walk off-site (same pattern as
    // DeleteAccountPage / ContactPage). This route carries no owning-parent
    // param — only `userMediaId` / `@path` — so the feed is the only fallback.
    const handleBack = useCallback(() => {
        Navigator.goBackOr(() => Navigator.navigateToFeed());
    }, []);

    // Show loading state only if fetching and not initialized. The shared shell
    // keeps the app bar ABOVE the spinner (never gated on `isInitialized`) so
    // the back affordance exists for the whole first fetch — see
    // `ContentPageFirstLoad` for the Flutter citation.
    if (!isInitialized && isLoading && !initialMedia) {
        return (
            <ContentPageFirstLoad
                ref={appBarRef}
                qa={{ id: 'content-details', status: 'loading' }}
                appBarQa={{ id: 'content-details-app-bar' }}
                leadingQa={{ id: 'content-details-back' }}
                onLeadingTap={handleBack}
            />
        );
    }

    return (
        <div {...qaAttr({ id: 'content-details', status: 'ready' })} className="w-full">
            {/* App bar — back chevron only, no title (Flutter renders
                `title: Container()`, content_list_widget.dart:1126). Sticky and
                translucent, so the content scrolls under it. */}
            <ExpauseAppBar
                ref={appBarRef}
                rootQa={{ id: 'content-details-app-bar' }}
                leadingQa={{ id: 'content-details-back' }}
                onLeadingTap={handleBack}
            />

            {/* Body — keeps the content's original `py-8` vertical rhythm; the
                padding moved off the page root so the bar sits flush at the top
                (and its own safe-area inset is not double-offset). */}
            <div className="py-8">
                {/* Error State — hidden when the media was handed to us via
                    location state, which renders regardless of the fetch. */}
                {error && !initialMedia && <ContentPageErrorCard onRetry={() => fetchMedia()} />}

                {/* Content */}
                <ContentListComponent
                    screenKey={screenKey}
                    qa={{ id: 'content-details-list' }}
                    isActive={isPageActive}
                    medias={media ? [media] : []}
                    currentUserId={currentUserId}
                    layout="list"
                    columns={1}
                    autoplay={true}
                    showFullscreenButton={true}
                    showMore={true}
                    muted={false}
                    isLoading={false}
                    hasMore={false}
                    sheetContainer={null}
                    // Keeps any `scrollToIndex` landing below the sticky bar.
                    scrollOffsetTop={appBarHeight}
                    gap={responsive.gap['6xl']}
                    emptyState={<ContentPageEmptyState title={l10n.content.contentNotFound} />}
                />
            </div>
        </div>
    );
};
